/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/
import React, {
  FunctionComponent,
  useCallback,
  useContext
} from "react";
import { IKeyEvent, KeyEvent } from "./KeyEvent";

export type Unregister = () => void;
export type OnKeyboardEvent = (event: globalThis.KeyboardEvent) => void;

export interface KeyboardEventActions {
  registerEvent: (keyEvent: IKeyEvent, func: OnKeyboardEvent) => Unregister;
}

export const KeyboardEventContext = React.createContext<KeyboardEventActions | undefined>(undefined);

export function useKeyboardEventContext(): KeyboardEventActions {
  const context = useContext(KeyboardEventContext);
  if (context === undefined) {
    throw new Error(
      "'useKeyboardEventContext' must be used within a KeyboardEventContextProvider"
    );
  }
  return context;
}

export const KeyboardEventContextProvider: FunctionComponent = ({ children }) => {

  const registerEvent = useCallback((keyEvent: IKeyEvent, func: OnKeyboardEvent) => {
    const key = KeyEvent.getHashCode(keyEvent);
    const onKeyDown = (ev: globalThis.KeyboardEvent) => {
      if (ev.key) {
        const evKey = KeyEvent.getHashCode(ev);
        if (key === evKey) {
          ev.preventDefault();
          func(ev);
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return (
    <KeyboardEventContext.Provider value={{ registerEvent }}>
      {children}
    </KeyboardEventContext.Provider>
  );
};
